import { NavLink, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { cn } from '@/lib/utils'
import { ChevronDown, Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { navigation, sectionForPath } from '@/lib/navigation'

const STORAGE_KEY = 'plutus.sidebar.expanded'

/**
 * Groups start collapsed except the one owning the current route (plus Home), so
 * ~50 pages stay navigable without a long scroll. The user's own expand/collapse
 * choices are remembered.
 */
function initialExpanded(pathname: string): string[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) return JSON.parse(saved)
  } catch { /* ignore unreadable storage */ }
  const active = sectionForPath(pathname)
  return ['Home', ...(active && active !== 'Home' ? [active] : [])]
}

export default function Sidebar() {
  const { user } = useAuthStore()
  const location = useLocation()
  const [expandedSections, setExpandedSections] = useState<string[]>(() => initialExpanded(location.pathname))

  // keep the active group open when navigating
  useEffect(() => {
    const active = sectionForPath(location.pathname)
    if (active) setExpandedSections(prev => (prev.includes(active) ? prev : [...prev, active]))
  }, [location.pathname])

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(expandedSections)) } catch { /* ignore */ }
  }, [expandedSections])

  const toggleSection = (title: string) =>
    setExpandedSections(prev => (prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]))

  const openPalette = () =>
    window.dispatchEvent(new CustomEvent('plutus:open-command-palette'))

  return (
    <div className="w-64 bg-card border-r border-border flex flex-col">
      {/* Logo */}
      <div className="h-16 flex items-center px-6 border-b border-border">
        <span className="text-xl font-semibold text-primary">Plutus</span>
      </div>

      {/* Search / command palette trigger */}
      <button
        onClick={openPalette}
        className="mx-3 mt-3 flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
      >
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">Search…</span>
        <kbd className="rounded border border-border px-1.5 py-0.5 text-[10px]">Ctrl K</kbd>
      </button>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3">
        {navigation.map((section) => {
          const visibleItems = section.items.filter(
            (item) => !item.adminOnly || user?.role === 'Admin'
          )
          if (visibleItems.length === 0) return null

          const isExpanded = expandedSections.includes(section.title)
          const hasActive = sectionForPath(location.pathname) === section.title

          return (
            <div key={section.title} className="mb-1">
              <button
                onClick={() => toggleSection(section.title)}
                className={cn(
                  'w-full flex items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:text-foreground',
                  hasActive ? 'text-primary' : 'text-muted-foreground'
                )}
              >
                <span className="flex items-center gap-2">
                  {section.title}
                  {!isExpanded && (
                    <span className="rounded-full bg-muted px-1.5 text-[10px] font-normal normal-case text-muted-foreground">
                      {visibleItems.length}
                    </span>
                  )}
                </span>
                <ChevronDown className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-180')} />
              </button>

              {isExpanded && (
                <div className="mt-1 space-y-1">
                  {visibleItems.map((item) => (
                    <NavLink
                      key={item.href}
                      to={item.href}
                      end={item.href === '/'}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 mx-2 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'text-foreground hover:bg-muted'
                        )
                      }
                    >
                      <item.icon className="h-4 w-4" />
                      {item.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      {/* Version */}
      <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground">
        PlutusPy v1.0.0
      </div>
    </div>
  )
}
